import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import { formatDateLocaleLima } from '../../../utils/dateUtils';

const HourMeters = () => {
  const [hourMeters, setHourMeters] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [machineFilter, setMachineFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [hourMeterToDelete, setHourMeterToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [hourMetersRes, machinesRes] = await Promise.all([
        machineryService.getHourMeters(),
        machineryService.getMachines()
      ]);
      
      if (hourMetersRes.success) {
        setHourMeters(hourMetersRes.data);
      }
      if (machinesRes.success) {
        setMachines(machinesRes.data);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al cargar los horómetros',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (hourMeter) => {
    setHourMeterToDelete(hourMeter);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!hourMeterToDelete) return;

    try {
      const response = await machineryService.deleteHourMeter(hourMeterToDelete.id);
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Registro de horómetro eliminado correctamente',
          type: 'success'
        });
        loadData();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el registro',
          type: 'error'
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al eliminar el registro',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setHourMeterToDelete(null);
    }
  };

  const filteredHourMeters = machineFilter === 'all' 
    ? hourMeters 
    : hourMeters.filter(hm => hm.machine_id === parseInt(machineFilter));

  // Ordenar por fecha descendente
  const sortedHourMeters = [...filteredHourMeters].sort((a, b) => 
    new Date(b.reading_date) - new Date(a.reading_date)
  );

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Horómetros</h1>
          <p className="text-gray-600 mt-1">Registro de horas de operación de máquinas</p>
        </div>
        <Link
          to="/machinery/hour-meters/create"
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          + Nuevo Registro
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Máquina
            </label>
            <select
              value={machineFilter}
              onChange={(e) => setMachineFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todas las máquinas</option>
              {machines.map((machine) => (
                <option key={machine.id} value={machine.id}>
                  {machine.code} - {machine.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Cargando horómetros...</p>
        </div>
      ) : sortedHourMeters.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No se encontraron registros de horómetros</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Máquina
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Horas Anteriores
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Horas Actuales
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Diferencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Operador
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedHourMeters.map((hourMeter) => (
                  <tr key={hourMeter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{hourMeter.machine_code}</div>
                      <div className="text-sm text-gray-500">{hourMeter.machine_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateLocaleLima(hourMeter.reading_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {parseFloat(hourMeter.previous_hours || 0).toLocaleString('es-PE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {parseFloat(hourMeter.hours || 0).toLocaleString('es-PE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-teal-600 font-medium">
                      +{parseFloat(hourMeter.hours_difference || 0).toLocaleString('es-PE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {hourMeter.operator || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/machinery/hour-meters/${hourMeter.id}/edit`}
                          className="text-teal-600 hover:text-teal-900"
                        >
                          Editar
                        </Link>
                        <button
                          onClick={() => handleDelete(hourMeter)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setHourMeterToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Eliminar Registro"
        message={`¿Estás seguro de eliminar este registro de horómetro?`}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default HourMeters;
